/*
 * Service Worker registrieren (PWA). Der Worker selbst wird beim Build von
 * Workbox erzeugt, Einstellungen in vue.config.js (→ workboxOptions).
 *
 * Eine neue Fassung übernimmt nicht von selbst, sondern wartet. index.vue
 * zeigt dann „Neue Version verfügbar“; erst der Knopf aktiviert sie und lädt
 * die Seite neu. Ausnahme ist der Kiosk (/club/today_monitor): Der läuft
 * dauerhaft, niemand drückt dort einen Knopf, und es gibt keine Eingaben,
 * die verloren gehen könnten. Er aktualisiert sich deshalb selbst.
 */
import Vue from 'vue';
import { register } from 'register-service-worker';

// Wie oft eine offene Seite nach einer neuen Fassung fragt
const UPDATE_CHECK_MS = 60 * 60 * 1000;

export const pwa = Vue.observable({
  updateReady: false,
  // Android/Chrome: gemerktes „beforeinstallprompt“, um die Installation
  // aus dem Hinweis heraus anzubieten
  installPrompt: null
});

const INSTALL_HINT_KEY = 'pwaInstallHintDismissed';

window.addEventListener('beforeinstallprompt', event => {
  event.preventDefault();
  pwa.installPrompt = event;
});
window.addEventListener('appinstalled', () => {
  pwa.installPrompt = null;
});

export function isIOS() {
  const ua = navigator.userAgent;
  // iPadOS meldet sich als Mac, hat aber einen Touchscreen
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function isMobile() {
  return isIOS() || /Android/.test(navigator.userAgent);
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
}

// Hinweis „Zum Home-Bildschirm hinzufügen“: nur auf dem Handy im Browser,
// nicht in der installierten App und nur, bis er weggeklickt wurde
export function shouldShowInstallHint() {
  if (!isMobile() || isStandalone()) return false;
  try {
    return !localStorage.getItem(INSTALL_HINT_KEY);
  } catch (error) {
    return false;
  }
}

export function dismissInstallHint() {
  try {
    localStorage.setItem(INSTALL_HINT_KEY, '1');
  } catch (error) {
    // localStorage nicht verfügbar
  }
}

export async function promptInstall() {
  const prompt = pwa.installPrompt;
  if (!prompt) return false;
  pwa.installPrompt = null;
  prompt.prompt();
  const { outcome } = await prompt.userChoice;
  return outcome === 'accepted';
}

let waitingRegistration = null;
let reloading = false;

export function isKiosk() {
  return window.location.pathname.startsWith('/club/today_monitor/');
}

export function applyUpdate() {
  const waiting = waitingRegistration && waitingRegistration.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'staging') {
    return;
  }
  if (!('serviceWorker' in navigator)) {
    return;
  }

  // Sobald die neue Fassung übernommen hat, einmal neu laden
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  register(`${process.env.BASE_URL}sw.js`, {
    registered(registration) {
      setInterval(() => registration.update().catch(() => {}), UPDATE_CHECK_MS);
    },
    updated(registration) {
      waitingRegistration = registration;
      if (isKiosk()) {
        applyUpdate();
      } else {
        pwa.updateReady = true;
      }
    },
    error(error) {
      console.error('Service Worker:', error);
    }
  });
}
